import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, PiggyBank, TrendingUp, Target, Umbrella, Sparkles,
  Clock, Lock, Gauge, ListChecks, ArrowRight, CheckCircle2,
  Wallet, GraduationCap, HeartPulse,
} from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteChrome";
import { useLang } from "../context/LanguageContext";
import { track, captureUTM } from "../lib/analytics";

const WHY_ICONS = [ShieldCheck, PiggyBank, TrendingUp, Target, Umbrella, Sparkles];
const STEP_ICONS = [ListChecks, Gauge, Target, Sparkles];
const DIM_ICONS = [ShieldCheck, PiggyBank, Umbrella, Wallet, TrendingUp, Target, HeartPulse, GraduationCap];

const HERO_BG =
  "https://images.unsplash.com/photo-1659352790654-058e9077a4f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBmYW1pbHklMjBmdXR1cmUlMjBwbGFubmluZ3xlbnwwfHx8fDE3ODYyOTIyNTl8MA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useLang();

  useEffect(() => { captureUTM(); track("landing_page_viewed"); }, []);
  const start = () => { track("assessment_started"); navigate("/assessment"); };

  const bullets = t("bullets");
  const why = t("why");
  const steps = t("steps");
  const dimensions = t("dimensions");
  const scoreAreas = t("score_areas");

  return (
    <div>
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(115deg, rgba(6,27,58,0.96) 0%, rgba(6,27,58,0.86) 45%, rgba(18,61,115,0.72) 100%)" }} />
          <div className="absolute inset-0 grain opacity-40" />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-xs tracking-[0.2em] uppercase text-gold font-semibold">
              {t("badge")}
            </motion.div>
            <motion.h1 initial="hidden" animate="show" variants={fadeUp} custom={1}
              className="mt-6 font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {t("hero_line1")}<br />
              <span className="text-gold">{t("hero_line2")}</span>
            </motion.h1>
            <motion.p initial="hidden" animate="show" variants={fadeUp} custom={2}
              className="mt-6 text-white/75 text-base md:text-lg max-w-xl leading-relaxed">
              {t("hero_sub")}
            </motion.p>

            <motion.ul initial="hidden" animate="show" variants={fadeUp} custom={3}
              className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 max-w-xl">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-white/85 text-sm">
                  <CheckCircle2 size={18} className="text-gold mt-0.5 shrink-0" /> {b}
                </li>
              ))}
            </motion.ul>

            <motion.div initial="hidden" animate="show" variants={fadeUp} custom={4}
              className="mt-10 flex flex-wrap gap-4">
              <button data-testid="hero-start-btn" onClick={start}
                className="group rounded-full bg-gold text-navy font-heading font-bold px-7 py-3.5 flex items-center gap-2 hover:-translate-y-0.5 hover:shadow-xl transition-transform duration-300">
                {t("cta_primary")}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <a href="#how" data-testid="hero-how-btn"
                className="rounded-full border border-white/25 text-white font-heading font-semibold px-7 py-3.5 hover:bg-white/10 transition-colors duration-300">
                {t("cta_secondary")}
              </a>
            </motion.div>
          </div>

          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={5} className="lg:col-span-5">
            <div className="rounded-3xl bg-white/95 backdrop-blur p-7 card-shadow border border-white/40">
              <div className="flex items-center gap-3">
                <div className="grid place-items-center h-12 w-12 rounded-2xl bg-navy"><Gauge className="text-gold" /></div>
                <div>
                  <div className="font-heading font-bold text-navy">{t("score_card_title")}</div>
                  <div className="text-xs text-slate-500">{t("score_card_sub")}</div>
                </div>
              </div>
              <div className="mt-5 space-y-3">
                {scoreAreas.map((l, i) => (
                  <div key={l}>
                    <div className="flex justify-between text-xs font-semibold text-slate-600 mb-1">
                      <span>{l}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${[70, 55, 85, 40, 60, 75][i]}%`, background: "linear-gradient(90deg,#123D73,#E9A21A)" }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-3 text-xs text-slate-500">
                <Lock size={14} className="text-brandgreen" /> {t("confidential_nologin")}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-6xl mx-auto px-5 py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="text-xs tracking-[0.2em] uppercase text-gold font-bold">{t("why_overline")}</div>
          <h2 className="mt-3 font-heading font-extrabold text-navy text-3xl md:text-4xl tracking-tight">
            {t("why_title")}
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {why.map((w, i) => {
            const Icon = WHY_ICONS[i];
            return (
              <motion.div key={w.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="group rounded-2xl bg-white border border-slate-200 p-7 card-shadow hover:-translate-y-1 transition-transform duration-300">
                <div className="grid place-items-center h-12 w-12 rounded-xl bg-navy/5 group-hover:bg-gold/15 transition-colors duration-300">
                  <Icon className="text-royal group-hover:text-gold transition-colors duration-300" />
                </div>
                <div className="mt-5 font-heading font-bold text-navy text-lg">{w.title}</div>
                <p className="mt-2 text-slate-500 text-sm leading-relaxed">{w.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="bg-navy relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative max-w-6xl mx-auto px-5 py-20 md:py-28">
          <div className="text-xs tracking-[0.2em] uppercase text-gold font-bold">{t("how_overline")}</div>
          <h2 className="mt-3 font-heading font-extrabold text-white text-3xl md:text-4xl tracking-tight max-w-xl">
            {t("how_title")}
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((st, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <motion.div key={st.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                  className="rounded-2xl bg-white/5 border border-white/10 p-7 hover:bg-white/10 transition-colors duration-300">
                  <div className="font-heading font-extrabold text-gold text-3xl">0{i + 1}</div>
                  <Icon className="text-white/80 mt-4" />
                  <div className="mt-4 font-heading font-bold text-white">{st.title}</div>
                  <p className="mt-2 text-white/60 text-sm leading-relaxed">{st.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-16 rounded-2xl border border-white/10 bg-white/[0.04] p-8">
            <div className="font-heading font-semibold text-white/90 mb-5">{t("dimensions_title")}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {dimensions.map((label, i) => {
                const Icon = DIM_ICONS[i];
                return (
                  <div key={label} className="flex items-center gap-3 text-white/75 text-sm">
                    <div className="grid place-items-center h-9 w-9 rounded-lg bg-white/5"><Icon size={16} className="text-gold" /></div>
                    {label}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-6xl mx-auto px-5 py-20 md:py-28">
        <div className="rounded-3xl brand-gradient relative overflow-hidden p-10 md:p-16 text-center card-shadow">
          <div className="absolute inset-0 grain opacity-30" />
          <div className="relative">
            <h2 className="font-heading font-extrabold text-white text-3xl md:text-5xl tracking-tight">
              {t("final_title")}
            </h2>
            <p className="mt-4 text-white/70 max-w-2xl mx-auto">
              {t("final_sub")}
            </p>
            <button data-testid="final-start-btn" onClick={start}
              className="mt-9 rounded-full bg-gold text-navy font-heading font-bold px-8 py-4 inline-flex items-center gap-2 hover:-translate-y-0.5 hover:shadow-xl transition-transform duration-300">
              {t("final_cta")} <ArrowRight size={18} />
            </button>
            <div className="mt-5 flex items-center justify-center gap-5 text-white/60 text-xs">
              <span className="flex items-center gap-1.5"><Clock size={14} /> {t("minutes")}</span>
              <span className="flex items-center gap-1.5"><Lock size={14} /> {t("confidential")}</span>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
